import { assertNever } from '@gitbook/util-ts';

import type { ContentOwnership } from './content';
import type {
    CustomHostname,
    CustomHostnamePointer,
    HostnamePointerAny,
    HostnamePointerOrganization,
} from './hostnames';

/**
 * Get a pointer for a content ownership.
 */
export function getHostnamePointerForOwner(
    ownership: ContentOwnership
): HostnamePointerOrganization {
    return { type: 'organization', organization: ownership.organization };
}

/**
 * RegExp to properly validate a custom domain following RFC 1035 guidelines
 * We also only support sub-domains (not apex)
 * https://www.ietf.org/rfc/rfc1035.txt
 */
const CUSTOM_HOSTNAMES_VALIDATION_REGEXP =
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.){2,}[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/;

/**
 * Check that a custom hostname is valid.
 */
export function validateCustomHostname(hostname: string): boolean {
    return (
        CUSTOM_HOSTNAMES_VALIDATION_REGEXP.test(hostname) &&
        hostname !== 'hosting.gitbook.com' &&
        !hostname.endsWith('gitbook.io') &&
        !hostname.endsWith('gitbooks.io') &&
        !hostname.endsWith('gitbook.me') &&
        // Too many users add a personal github.io domain, but this can't work...
        !hostname.endsWith('github.io')
    );
}

/**
 * Return the custom hostname pointer
 */
export function getCustomHostnamePointer(customHostname: CustomHostname): CustomHostnamePointer {
    switch (customHostname.type) {
        case 'organization':
            return {
                type: customHostname.type,
                organization: customHostname.organization,
            };
        case 'site':
            return {
                type: customHostname.type,
                site: customHostname.site,
            };
        default:
            assertNever(customHostname);
    }
}

/**
 * Return the identifier to the content behind a pointer.
 */
export function getHostnamePointerContentID(pointer: HostnamePointerAny): string {
    switch (pointer.type) {
        case 'organization':
            return pointer.organization;
        case 'site':
            return pointer.site;
        default:
            assertNever(pointer);
    }
}

/**
 * Return true if both pointers are identical.
 */
export function compareHostnamePointer(a: HostnamePointerAny, b: HostnamePointerAny): boolean {
    return (
        (a.type === 'organization' &&
            b.type === 'organization' &&
            a.organization === b.organization) ||
        (a.type === 'site' && b.type === 'site' && a.site === b.site)
    );
}

/**
 * Return a pretty string for a custom hostname pointer.
 */
export function getCustomHostnamePointerPrettyTarget(pointer: CustomHostnamePointer): string {
    switch (pointer.type) {
        case 'organization':
            return `organization "${pointer.organization}"`;
        case 'site':
            return `site "${pointer.site}"`;
        default:
            return assertNever(pointer);
    }
}
